import React from 'react';
import { applyScanner } from '../../utils/scan';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'online', label: 'Online' },
  { key: 'offline', label: 'Offline' },
  { key: 'idle', label: 'Idle' },
  { key: 'RH2', label: 'RH2' },
  { key: 'RH1', label: 'RH1' },
  { key: 'Teltonika', label: 'Teltonika' },
];

const SearchIcon = () => (
  <svg className="input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export default function DeviceListPanel({
  devices, mappableCount = 0, selectedDeviceId, onSelectDevice,
  searchQuery, onSearchChange,
  activeFilter, onFilterChange,
  locationFilter, onLocationChange, locationOptions = [],
  batchFilter, onBatchChange, batchOptions = [],
  panelVisible
}) {
  return (
    <div
      className={`surface-float dm-panel${panelVisible ? '' : ' collapsed'}`}
      aria-hidden={panelVisible ? undefined : true}
    >
      <div className="dm-panel-header">
        <div className="dm-panel-title">
          <h3>Devices</h3>
          <span className="dm-panel-count">
            {devices.length} device{devices.length === 1 ? '' : 's'}
            {devices.length > 0 && mappableCount < devices.length && (
              <> · {mappableCount} on map</>
            )}
          </span>
        </div>

        <div className="input-with-icon">
          <SearchIcon />
          <input
            className="input dm-select"
            type="text"
            placeholder="Search devices…"
            aria-label="Search devices"
            value={searchQuery}
            onChange={e => onSearchChange(applyScanner(searchQuery, e.target.value, 'replace'))}
          />
        </div>

        <div className="dm-filters" role="group" aria-label="Filter devices by status and type">
          {FILTERS.map(f => {
            const isActive = activeFilter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                className={`pill ${isActive ? 'pill-brand' : 'pill-neutral'} dm-chip${isActive ? ' active' : ''}`}
                onClick={() => onFilterChange(f.key)}
                aria-pressed={isActive}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <select
          className="input dm-select"
          value={locationFilter}
          onChange={e => onLocationChange(e.target.value)}
          aria-label="Filter by site location"
        >
          <option value="all">All locations</option>
          {locationOptions.map(loc => (
            <option key={loc} value={loc}>{loc}</option>
          ))}
        </select>

        {batchOptions.length > 0 && (
          <select
            className="input dm-select"
            value={batchFilter}
            onChange={e => onBatchChange(e.target.value)}
            aria-label="Filter by import batch"
          >
            <option value="all">All batches</option>
            {batchOptions.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        )}
      </div>

      <div className="dm-device-list">
        {devices.length > 0 && mappableCount === 0 && (
          <div className="dm-advisory">
            None of these devices have reported a GPS position, so the map has nothing to plot.
            Devices in stock or with the manufacturer typically have no fix.
          </div>
        )}
        {devices.map(d => (
          <button
            key={d.id}
            type="button"
            className={`dm-device-card${selectedDeviceId === d.id ? ' selected' : ''}`}
            onClick={() => onSelectDevice(d.id)}
            aria-pressed={selectedDeviceId === d.id}
          >
            {/* Row 1 mirrors the design: dot, serial, then last-seen pushed
                right. The Lightbug deviceId is deliberately not repeated here
                — `name` already resolves to the serial, so showing both put
                the same number on the row twice. */}
            <div className="dm-card-row1">
              <span className={`status-dot dot-lg dot-${d.status}`} />
              <span className="dm-card-name">{d.name}</span>
              <span className="dm-card-time">{d.lastSeen}</span>
            </div>
            <div className="dm-card-row2">
              <span className={`chip dm-type-chip ${d.type.toLowerCase()}`}>{d.type}</span>
              {d.location && <span className="dm-card-location">{d.location}</span>}
              {d.client && <span className="dm-card-client">{d.client}</span>}
            </div>
          </button>
        ))}
        {devices.length === 0 && (
          <div className="dm-list-empty">
            {activeFilter === 'all' && locationFilter === 'all' && batchFilter === 'all' && !searchQuery
              ? 'No devices with GPS data available. Devices will appear here once Lightbug reports their positions.'
              : 'No devices match your filters'}
          </div>
        )}
      </div>
    </div>
  );
}
